import { AppDispatch } from 'config/redux'
import { isAiPopType } from 'redux/actions/pops'
import { AiPopForm, PopFormValues } from 'components/PopCreation/types'
import { CompaignFormValues } from 'components/AdminConsole/Compaign/CompaignModal'
import { Job } from 'redux/actions/admin'
import { getPrompt } from 'redux/actions/promts'

export type ServiceActionParams = PopFormValues | CompaignFormValues | null

export interface ServiceHandlerConfig {
  onCreated: (data: ServiceActionParams) => void
  clearError: (field: string) => void
  dispatch: AppDispatch
  setIsLoading: (loading: boolean) => void
  openPopModal?: (config: any) => void
  openCompaignModal?: (opts?: {
    onCreated?: (data: CompaignFormValues) => void
    initialData?: CompaignFormValues | null
  }) => void
}

export interface ServiceHandler {
  handleCreate: (actionParams: ServiceActionParams, config: ServiceHandlerConfig) => Promise<void> | void
  isDisabledForEdit: (actionParams: ServiceActionParams, job?: Job | null) => boolean
}

class PopsServiceHandler implements ServiceHandler {
  async handleCreate(actionParams: ServiceActionParams, config: ServiceHandlerConfig) {
    const { onCreated, clearError, dispatch, setIsLoading, openPopModal } = config

    if (!openPopModal) {
      console.error('openPopModal hook not provided for PopsServiceHandler')
      return
    }

    if (!actionParams) {
      // Open modal to create a new pop
      return openPopModal({
        onCreated: (createdPopData: PopFormValues) => {
          onCreated(createdPopData)
          clearError('job_data.action_params')
        },
        validateOnly: true,
        initialPopData: null,
      })
    }

    let updatedParams = actionParams as PopFormValues

    if (isAiPopType(updatedParams?.pop_type)) {
      const aiPopData = updatedParams as AiPopForm
      const promptId = aiPopData.data.prompt_id
      try {
        setIsLoading(true)
        const prompt = await dispatch(getPrompt({ prompt_id: promptId }))
        updatedParams = {
          ...updatedParams,
          data: {
            ...aiPopData.data,
            pop_body: prompt,
          },
        } as PopFormValues
      } catch (error) {
        console.error('Error fetching prompt', error)
      } finally {
        setIsLoading(false)
      }
    }

    openPopModal({
      onCreated: (data: PopFormValues) => {
        onCreated(data)
        clearError('job_data.action_params')
      },
      validateOnly: true,
      initialPopData: updatedParams,
    })
  }

  isDisabledForEdit(actionParams: ServiceActionParams, job?: Job | null): boolean {
    return false // Pops can always be edited
  }
}

class CompaignServiceHandler implements ServiceHandler {
  handleCreate(actionParams: ServiceActionParams, config: ServiceHandlerConfig) {
    const { onCreated, clearError, openCompaignModal } = config

    if (!openCompaignModal) {
      console.error('openCompaignModal hook not provided for CompaignServiceHandler')
      return
    }

    // Передаем существующие данные кампании, если они есть
    const existingCampaignData = actionParams as CompaignFormValues | null

    openCompaignModal({
      onCreated: (data: CompaignFormValues) => {
        onCreated(data)
        clearError('job_data.action_params')
      },
      initialData: existingCampaignData,
    })
  }

  isDisabledForEdit(actionParams: ServiceActionParams, job?: Job | null): boolean {
    // Campaign is disabled only when editing an existing job with action_service = 'campaign'
    return !!(job && job.action_service === 'campaign')
  }
}

// Registry of all service handlers
const SERVICE_HANDLERS: Record<string, ServiceHandler> = {
  pops: new PopsServiceHandler(),
  campaigns: new CompaignServiceHandler(),
}

export class ServiceHandlersManager {
  static getHandler(serviceType: string): ServiceHandler | null {
    return SERVICE_HANDLERS[serviceType] || null
  }

  static async handleServiceAction(
    serviceType: string,
    actionParams: ServiceActionParams,
    config: ServiceHandlerConfig
  ): Promise<void> {
    const handler = this.getHandler(serviceType)
    if (!handler) {
      console.warn(`No handler found for service type: ${serviceType}`)
      return
    }

    await handler.handleCreate(actionParams, config)
  }

  static isServiceDisabledForEdit(serviceType: string, actionParams: ServiceActionParams, job?: Job | null): boolean {
    const handler = this.getHandler(serviceType)
    if (!handler) return false

    return handler.isDisabledForEdit(actionParams, job)
  }

  static getSupportedServices(): string[] {
    return Object.keys(SERVICE_HANDLERS)
  }
}
